import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Project } from '../../../../models';
import { PublicChanges, type PublicChangeData } from '../../../../services';
import { Icon } from '../../../../components';
import { useAppSelector, RootState } from '../../../../redux';

function PublicChangeView() {
    const { id }       = useParams();
    const { i18n, t }  = useTranslation();
    const navigate     = useNavigate();
    const { user }     = useAppSelector((state: RootState) => state.auth);

    const [change, setChange]     = useState<PublicChangeData | null>(null);
    const [loading, setLoading]   = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        if (!id) return;
        setLoading(true);
        PublicChanges.getPublic(id)
            .then(data => {
                setChange(data);
                setLoading(false);
            })
            .catch(() => {
                setNotFound(true);
                setLoading(false);
            });
    }, [id]);

    // If authenticated and we know the project, go straight to the private view.
    useEffect(() => {
        if (user?._id && change?.project?._id && id)
            navigate(Project.path(change.project, 'change', id), { replace: true });
    }, [user, change, id, navigate]);

    const lang        = i18n.language;
    const title       = change?.title?.[lang] ?? change?.title?.fr ?? change?.title?.en ?? '';
    const description = change?.description?.[lang] ?? change?.description?.fr ?? change?.description?.en ?? '';

    useEffect(() => {
        if (!change) return;

        document.title = title ? `Yoda - ${title}` : 'Yoda';

        const setMeta = (property: string, content: string) => {
            let el = document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
            if (!el) {
                el = document.createElement('meta');
                el.setAttribute('property', property);
                document.head.appendChild(el);
            }
            el.setAttribute('content', content);
        };

        const plainDesc = description.replace(/<[^>]+>/g, '').slice(0, 200);
        setMeta('og:title',       title);
        setMeta('og:description', plainDesc);
        setMeta('og:type',        'website');
        setMeta('og:url',         window.location.href);

        return () => { document.title = 'Yoda'; };
    }, [title, description, change]);

    if (loading) {
        return (
            <div className="flex justify-center items-center p-12">
                <Icon type="loading" size={10} />
            </div>
        );
    }

    if (notFound || !change) {
        return (
            <div className="flex flex-col justify-center items-center p-12 gap-4 text-gray-500 dark:text-gray-400">
                <Icon type="sad" size={10} />
                <p className="text-lg">Card not found.</p>
            </div>
        );
    }

    return (
        <div className="PublicChangeView max-w-2xl mx-auto px-4 py-10">
            {change.project?.name && (
                <p className="text-sm text-gray-400 mb-1 uppercase tracking-wide">
                    {change.project.name}
                </p>
            )}
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">{title}</h1>
            {description && (
                <div
                    className="prose prose-sm text-gray-700 dark:text-gray-200 mb-8"
                    dangerouslySetInnerHTML={{ __html: description }}
                />
            )}
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                    {t('common.login_to_see_more') || 'Log in to access the full card with comments, attachments and history.'}
                </p>
                <button
                    onClick={() => navigate('/')}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white text-sm font-medium rounded hover:bg-cyan-700 transition-colors"
                >
                    <Icon type="user" size={4} />
                    {t('login.login') || 'Log in'}
                </button>
            </div>
        </div>
    );
}

export default PublicChangeView;
